import {
  priceProvidersRepositoryAbi,
  siloLensAbi,
} from "@silo/contract-abi-types";
import {
  priceProvidersRepositoryAddress,
  siloLensAddress,
} from "@silo/contract-addresses";
import {
  Address,
  AmountFromBigInt,
  ChainPublicClientService,
  chains,
  getChainKeyFromProtocol,
  PercentFromBigInt,
  ProtocolKeyService,
  ProtocolStateService,
} from "@silo/types";
import { Schema } from "@effect/schema";
import { Effect, pipe } from "effect";
import { getContract } from "viem";
import { convertToUsd, getCollateralBaseApr } from "@silo/utils";
import { ContractsClientError } from "../contracts-client/types";
import { requireAnySettled, settledOrZero } from "../utils/settle-reverts";

interface GetDashboardAssetStateParams {
  marketAddress: Address;
  tokenAddress: Address;
}

/**
 * Dashboard-only asset state fetcher.
 *
 * Fetches the minimum set of on-chain reads the dashboard UI actually renders:
 * - oracle price (native + USD)
 * - deposit APY (after protocol share fee)
 * - borrow APY
 *
 * Skips: liquidity, utilization, collateralTotal, protectedTotal, debtTotal,
 * isSiloPaused, rewards emissions. Those fields are either unused by the
 * dashboard or no longer surfaced.
 */
export function getDashboardAssetState({
  marketAddress,
  tokenAddress,
}: GetDashboardAssetStateParams) {
  return Effect.gen(function* () {
    const protocolKey = yield* ProtocolKeyService;
    const { publicClient } = yield* ChainPublicClientService;
    const { protocolShareFee, quotePriceUsd } = yield* ProtocolStateService;

    const priceProvidersRepositoryContract = getContract({
      address: priceProvidersRepositoryAddress[protocolKey],
      abi: priceProvidersRepositoryAbi,
      client: publicClient,
    });

    const siloLensContract = getContract({
      address: siloLensAddress[protocolKey],
      abi: siloLensAbi,
      client: publicClient,
    });

    const encoded = yield* Effect.tryPromise({
      try: async () => {
        const results = requireAnySettled(
          await Promise.allSettled([
            priceProvidersRepositoryContract.read.getPrice([tokenAddress]),
            siloLensContract.read.depositAPY([marketAddress, tokenAddress]),
            siloLensContract.read.borrowAPY([marketAddress, tokenAddress]),
          ])
        );

        const [priceNative, rawCollateralBaseApr, debtBaseApr] =
          results.map(settledOrZero);

        return {
          priceNative,
          rawCollateralBaseApr,
          debtBaseApr,
        };
      },
      catch: (cause) => new ContractsClientError({ cause }),
    });

    const { priceNative, rawCollateralBaseApr, debtBaseApr } = yield* pipe(
      encoded,
      Schema.decode(DashboardAssetStateEncoded)
    );

    const collateralBaseApr = getCollateralBaseApr(
      protocolShareFee,
      rawCollateralBaseApr
    );

    const chainKey = getChainKeyFromProtocol(protocolKey);
    const { nativeCurrency } = chains[chainKey];

    const priceUsd = convertToUsd(
      priceNative,
      nativeCurrency.decimals,
      quotePriceUsd
    );

    return {
      priceNative,
      priceUsd,
      collateralBaseApr,
      debtBaseApr,
    };
  }).pipe(
    Effect.tapError((error) =>
      Effect.logError(
        `Failed ❌: getDashboardAssetState market=${marketAddress} token=${tokenAddress}: ${
          (error as { _tag?: string })._tag ?? String(error)
        }`
      )
    )
  );
}

const DashboardAssetStateEncoded = Schema.Struct({
  priceNative: AmountFromBigInt,
  rawCollateralBaseApr: PercentFromBigInt,
  debtBaseApr: PercentFromBigInt,
}).annotations({ identifier: "DashboardAssetStateEncoded" });
